"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseEntityUriComponents = exports.createEntityUriMultiple = exports.createEntityUri = exports.entityUriRegex = exports.globalEntityUriRegex = exports.IDENTIFIER_ENTITY_NAME = void 0;
exports.getExpectedScheme = getExpectedScheme;
exports.parseEntityUri = parseEntityUri;
exports.parseEntityUriMultiple = parseEntityUriMultiple;
exports.IDENTIFIER_ENTITY_NAME = {
    Actor: 'actor',
    ActorFile: 'actor-file',
    Workflow: 'workflow',
    File: 'db-file', // special name since file::uuid are not valid entity URIs
    ActorVoice: 'actor-voice',
    StudioProject: 'studio-project',
    AudioFile: 'audio-file',
    ActorLipSync: 'actor-lip-sync',
    AdvertiserBrand: 'advertiser-brand',
    Ad: 'ad',
    AgentReport: 'agent-report',
    ContextFile: 'context-file',
    // A working document (ContextFile with fileType DOCUMENT) — same table as
    // context-file://, distinct scheme so paperwork and brand knowledge stay
    // separate concepts in agent prompts and UI rendering.
    Document: 'document',
    Product: 'product',
    // Manager coordination entity; previously a prompt-only convention, now
    // first-class so content link-sync can parse it.
    ManagerInitiative: 'agent-initiative',
    // Manager memory — the agency's own durable knowledge of a brand (the
    // ManagerMemory table). Internal scheme: linked in briefs/documents/dispatches,
    // never handed into the user's chat (the document:// rule). Unrelated to the
    // orchestrator's AgentMemory* system.
    ManagerMemory: 'memory',
    Generation: 'generation',
    Creative: 'creative',
    Placement: 'placement',
};
const IDENTIFIER_ENTITY_SCHEMA_FALLBACK = {
    'actor-voice': ['voice://'],
};
function createIdentifierString(entity, uuid) {
    return `${entity}://${uuid}`;
}
const uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const onlyUuidRegex = new RegExp(`^${uuidRegex}$`, 'i');
/**
 * Global regex that matches all occurrences of an entity URI in a string (not anchored).
 * Useful for scanning text like markdown content for embedded entity URIs.
 */
const globalEntityUriRegex = (entity) => {
    return new RegExp(`${entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:\\/\\/${uuidRegex}`, 'gi');
};
exports.globalEntityUriRegex = globalEntityUriRegex;
const entityUriRegex = (entity) => {
    if (Array.isArray(entity)) {
        return new RegExp(`^(${entity.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')}):\/\/${uuidRegex}$`, 'i');
    }
    return new RegExp(`^${entity}:\/\/${uuidRegex}$`, 'i');
};
exports.entityUriRegex = entityUriRegex;
function getExpectedScheme(entity) {
    return `${entity}://`;
}
function parseEntityUri(identifier, entity) {
    if (!identifier)
        return undefined;
    const entitiesArray = Array.isArray(entity) ? entity : [entity];
    // Define the expected scheme for each entity type
    const expectedSchemes = entitiesArray.map(getExpectedScheme);
    const foundExpectedScheme = expectedSchemes.find((expectedScheme) => identifier.startsWith(expectedScheme));
    // Check if identifier starts with the correct scheme
    if (!foundExpectedScheme) {
        // Check for fallback schemas if the identifier does not start with the expected scheme
        for (const [_, fallbackSchemaOptions] of Object.entries(IDENTIFIER_ENTITY_SCHEMA_FALLBACK)) {
            const fallbackSchema = fallbackSchemaOptions.find((fallbackSchema) => identifier.startsWith(fallbackSchema));
            if (fallbackSchema) {
                // Replace the fallback schema with the expected schema and parse the identifier
                return parseEntityUri(identifier.replace(fallbackSchema, entitiesArray[0]), entity);
            }
        }
        throw new Error(`Invalid identifier scheme. Expected schema ${expectedSchemes.join(' or ')}, got ${identifier}`);
    }
    // Extract the UUID part (everything after the scheme)
    const uuid = identifier.slice(foundExpectedScheme.length);
    // Validate UUID format using regex
    if (!onlyUuidRegex.test(uuid)) {
        throw new Error(`Invalid UUID format: ${uuid}`);
    }
    return uuid;
}
const createEntityUri = (uuid, entity) => {
    // Validate UUID format first
    if (!onlyUuidRegex.test(uuid)) {
        throw new Error(`Invalid UUID format: ${uuid}`);
    }
    return createIdentifierString(entity, uuid);
};
exports.createEntityUri = createEntityUri;
/**
 * Create an entity URI with multiple UUIDs (comma-separated).
 * Format: entity://uuid1,uuid2,uuid3
 */
const createEntityUriMultiple = (uuids, entity) => {
    if (uuids.length === 0) {
        throw new Error('At least one UUID is required');
    }
    // Validate all UUIDs
    for (const uuid of uuids) {
        if (!onlyUuidRegex.test(uuid)) {
            throw new Error(`Invalid UUID format: ${uuid}`);
        }
    }
    return `${entity}://${uuids.join(',')}`;
};
exports.createEntityUriMultiple = createEntityUriMultiple;
/**
 * Parse an entity URI that may contain multiple comma-separated UUIDs.
 * Returns an array of UUIDs.
 * Supports fallback schemas (e.g., voice:// → actor-voice://).
 */
function parseEntityUriMultiple(identifier, entity) {
    const entitiesArray = Array.isArray(entity) ? entity : [entity];
    const expectedSchemes = entitiesArray.map(getExpectedScheme);
    let foundExpectedScheme = expectedSchemes.find((expectedScheme) => identifier.startsWith(expectedScheme));
    // Check for fallback schemas if the identifier does not start with the expected scheme
    if (!foundExpectedScheme) {
        for (const [_, fallbackSchemaOptions] of Object.entries(IDENTIFIER_ENTITY_SCHEMA_FALLBACK)) {
            const fallbackSchema = fallbackSchemaOptions.find((fallbackSchema) => identifier.startsWith(fallbackSchema));
            if (fallbackSchema) {
                // Replace the fallback schema with the expected schema and parse recursively
                return parseEntityUriMultiple(identifier.replace(fallbackSchema, getExpectedScheme(entitiesArray[0])), entity);
            }
        }
        throw new Error(`Invalid identifier scheme. Expected schema ${expectedSchemes.join(' or ')}, got ${identifier}`);
    }
    // Extract the UUID part (everything after the scheme)
    const uuidPart = identifier.slice(foundExpectedScheme.length);
    // Split by comma and validate each UUID
    const uuids = uuidPart.split(',');
    for (const uuid of uuids) {
        if (!onlyUuidRegex.test(uuid)) {
            throw new Error(`Invalid UUID format: ${uuid}`);
        }
    }
    return uuids;
}
const parseEntityUriComponents = (identifier) => {
    const [entity] = identifier.split('://');
    if (!guardIsEntityIdentifier(entity)) {
        throw new Error('Invalid entity identifier');
    }
    const uuid = parseEntityUri(identifier, entity);
    return { entity, uuid };
};
exports.parseEntityUriComponents = parseEntityUriComponents;
const guardIsEntityIdentifier = (identifier) => {
    return Object.values(exports.IDENTIFIER_ENTITY_NAME).includes(identifier);
};
